import { SubscriptionTypeValue } from '@app/utils/consts.tsx';

interface PricingPlan {
  id: number;
  type: 'FREE' | 'PRO' | 'GROWTH';
  reviewRequestLimit: number;
  seatLimit: number;
  price: number;
  priceId: string;
  currency: string;
  features: PlanFeature[];
}

interface PlanFeature {
  id: number;
  name: string;
  planId: number;
}

interface PricingPlansProps {
  selectedPlan: string | null;
  onPlanSelect: (planId: 'starter' | 'professional' | 'enterprise') => void;
  showTrialOffer?: boolean;
  plans?: PricingPlan[];
  buttonText?: string;
  onPlanTypeSelect: (planType: SubscriptionTypeValue) => void;
}

// @ts-ignore
export const PricingPlans = ({ selectedPlan, onPlanSelect, showTrialOffer = true, plans, buttonText = 'Start Free', onPlanTypeSelect }: PricingPlansProps) => {
  // Default plans if none provided (for backward compatibility)
  const defaultPlans: PricingPlan[] = [
    {
      id: 1,
      type: 'FREE',
      reviewRequestLimit: 300,
      seatLimit: 1,
      price: 47,
      priceId: 'price_basic',
      currency: 'usd',
      features: [
        { id: 1, name: '1 Member Seat', planId: 1 },
        { id: 2, name: 'Up to 300 Outreach Attempts', planId: 1 },
      ],
    },
    {
      id: 2,
      type: 'PRO',
      reviewRequestLimit: 1000,
      seatLimit: 5,
      price: 97,
      priceId: 'price_pro',
      currency: 'usd',
      features: [
        { id: 7, name: 'Up to 5 Member Seats', planId: 2 },
        { id: 8, name: 'Up to 1000 Outreach Attempts', planId: 2 },
      ],
    },
    {
      id: 3,
      type: 'GROWTH',
      reviewRequestLimit: 10000,
      seatLimit: 20,
      price: 127,
      priceId: 'price_enterprise',
      currency: 'usd',
      features: [
        { id: 18, name: 'Up to 20 Member Seats', planId: 3 },
        { id: 19, name: 'Up to 10000 Outreach Attempts', planId: 3 },
      ],
    },
  ];

  const pricingPlans = plans || defaultPlans;

  // Get features to display for each plan type
  const getFeaturesForPlan = (planType: string) => {
    const plan = pricingPlans.find((p) => p.type === planType);
    const planFeatures = plan?.features || [];

    if (planType === 'FREE') {
      // Basic plan: show its features + grayed out higher-tier features
      const grayedOutFeatures = [
        'AI Assisted Customer Reviews',
        'AI Assisted Responses',
        'AI Assisted Review Outreach and Follow up',
        'CRM syncronization',
        'Basic review analytics',
        'Advanced review analytics',
        '24/7 Support Desk',
        'Dedicated success agent',
      ];

      // Combine plan features with grayed out features (avoiding duplicates)
      const allFeatureNames = new Set([...planFeatures.map((f) => f.name), ...grayedOutFeatures]);

      return Array.from(allFeatureNames).map((name) => ({
        name,
        isIncluded: planFeatures.some((f) => f.name === name),
      }));
    }

    if (planType === 'PRO') {
      // Professional plan: show its features + grayed out enterprise features
      const grayedOutFeatures = ['Advanced review analytics', '24/7 Support Desk', 'Dedicated success agent'];

      // Combine plan features with grayed out features (avoiding duplicates)
      const allFeatureNames = new Set([...planFeatures.map((f) => f.name), ...grayedOutFeatures]);

      return Array.from(allFeatureNames).map((name) => ({
        name,
        isIncluded: planFeatures.some((f) => f.name === name),
      }));
    }

    if (planType === 'GROWTH') {
      // Enterprise plan: show only its included features
      return planFeatures.map((feature) => ({
        name: feature.name,
        isIncluded: true,
      }));
    }

    return [];
  };

  // Helper function to get plan display info
  const getPlanInfo = (type: string) => {
    switch (type) {
      case 'FREE':
        return {
          id: 'starter',
          name: 'Free Plan',
          audience: 'For Small Businesses and Local Brands',
          popular: false,
          bestValue: false,
        };
      case 'PRO':
        return {
          id: 'professional',
          name: 'Pro Plan',
          audience: 'For Growing Teams and Multi-Location Businesses',
          popular: true,
          bestValue: false,
        };
      case 'GROWTH':
        return {
          id: 'enterprise',
          name: 'Growth Plan',
          audience: 'For Enterprises, Agencies, and Franchises',
          popular: false,
          bestValue: true,
        };
      default:
        return {
          id: 'starter',
          name: 'Plan',
          audience: '',
          popular: false,
          bestValue: false,
        };
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-blue-100 py-12 px-4 rounded-2xl mb-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pricingPlans.map((plan) => {
            const planInfo = getPlanInfo(plan.type);
            return (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transition-all duration-300 hover:shadow-xl border-2 cursor-pointer ${
                  selectedPlan === planInfo.id ? 'border-brand-baseBlue shadow-xl' : 'border-gray-200'
                }`}
                onClick={() => {
                  onPlanSelect(planInfo.id as any);
                  onPlanTypeSelect(plan.type);
                }}
              >
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-2xl font-bold text-gray-900">{planInfo.name}</h3>
                    {/* Most Popular Badge */}
                    {planInfo.popular && (
                      <div className="bg-brand-baseBlue text-white px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1">
                        MOST POPULAR
                        <span>🚀</span>
                      </div>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm mb-4">{planInfo.audience}</p>
                  <div className="border-b border-brand-baseBlue w-full mx-auto my-4"></div>
                  Trial Offer - Only for Professional
                  {planInfo.popular && showTrialOffer && (
                    <div className="flex items-center justify-center gap-1 mb-4 text-sm text-amber-600">
                      <span>🎁</span>
                      <span>Free 14-day trial – no credit card required</span>
                    </div>
                  )}
                  {/* Price */}
                  <div className="mb-4">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-4xl font-bold text-blue-600">${plan.price}</span>
                      <span className="text-gray-500">/mo</span>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-sm text-orange-500 font-semibold">
                      <span>🔥</span>
                      <span>50% Off - Early Access Pricing</span>
                    </div>
                  </div>
                  {/* CTA Button */}
                  <button className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${planInfo.popular ? 'bg-orange-500 text-white' : 'bg-brand-baseBlue text-white'}`}>
                    {buttonText} &gt;
                  </button>
                </div>

                {/* Features List */}
                <div className="space-y-0">
                  {getFeaturesForPlan(plan.type).map((feature, index) => (
                    <div key={index} className="min-h-[48px] py-1 pl-8">
                      <div className="relative">
                        <div className="absolute left-[-32px] top-1">
                          {feature.isIncluded ? (
                            <div className="w-5 h-5 rounded-full bg-brand-baseBlue flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                        <span className={`text-sm leading-5 text-left block ${feature.isIncluded ? 'text-gray-700' : 'text-gray-400'}`}>{feature.name}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom Note */}
                <div className="mt-8">
                  <div className="flex items-start justify-center gap-1 text-sm text-gray-600">
                    {planInfo.popular ? (
                      <>
                        <span>🚀</span>
                        <span>All the power, without complexity. New features launching regularly</span>
                      </>
                    ) : planInfo.id === 'starter' ? (
                      <>
                        <span className="text-yellow-500 text-lg -mt-1">👆</span>
                        <span>Great if you're just starting and want to test the waters. New features launching regularly</span>
                      </>
                    ) : (
                      <>
                        <span className="text-yellow-500 text-lg -mt-1">👆</span>
                        <span>Designed for scale, control, and brand consistency. New features launching regularly</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center space-y-4 mt-10">
        <p className="text-sm text-gray-500">*Early access pricing valid for the first 100 users. Prices subject to change.</p>
      </div>
    </div>
  );
};
